import { useEffect, useRef } from "react";

function getMasteryLabel(status) {
    if (status === "mastered") return "Mastered";
    if (status === "developing") return "Developing";
    if (status === "needs_focus") return "Needs Focus";
    return "Not Started";
}

function getMasteryColorByStatus(status) {
    if (status === "mastered") return "#22c55e";
    if (status === "developing") return "#f59e0b";
    if (status === "needs_focus") return "#ef4444";
    return "#94a3b8";
}

export function DonutArc({ masteredFrames, totalFrames, overallStatus, size = 180 }) {
    const pct = totalFrames > 0 ? Math.round((masteredFrames / totalFrames) * 100) : 0;
    const r = 70;
    const cx = 90;
    const cy = 90;
    const stroke = 10;
    const startAngle = -90;
    const sweepTotal = 359.99; // 359.99 avoids SVG arc 360-degree quirk
    const sweepFill = (pct / 100) * sweepTotal;

    function polar(angle, radius) {
        const rad = (angle * Math.PI) / 180;
        return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
    }

    function arcPath(startDeg, sweepDeg) {
        // If sweep is 0, just return empty so it doesn't break
        if (sweepDeg <= 0) return "";
        const start = polar(startDeg, r);
        const end = polar(startDeg + sweepDeg, r);
        const large = sweepDeg > 180 ? 1 : 0;
        return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
    }

    const label = getMasteryLabel(overallStatus || (pct === 0 ? "not_started" : "developing"));
    const fillClr = getMasteryColorByStatus(overallStatus);

    return (
        <svg width={size} height={size} viewBox="0 0 180 180">
            <path d={arcPath(startAngle, sweepTotal)} fill="none" stroke="#e2e8f0" strokeWidth={stroke} strokeLinecap="round" />
            {pct > 0 && (
                <path
                    d={arcPath(startAngle, sweepFill)}
                    fill="none"
                    stroke={fillClr}
                    strokeWidth={stroke}
                    strokeLinecap="round"
                />
            )}
            <text x={cx} y={cy - 10} textAnchor="middle" fontSize="24" fontWeight="700" fill="#0f172a">
                {pct}%
            </text>
            <text x={cx} y={cy + 14} textAnchor="middle" fontSize="13" fontWeight="600" fill={fillClr}>
                {label}
            </text>
        </svg>
    );
}

const DIFF_META = {
    LOT: { label: "LOT", sub: "Easy", color: "#22c55e" },
    MOT: { label: "MOT", sub: "Medium", color: "#f59e0b" },
    HOT: { label: "HOT", sub: "Hard", color: "#ef4444" },
};

function DiffRow({ diff, stats, totalCount }) {
    const meta = DIFF_META[diff];
    const total = totalCount ?? 0;
    const mastered = stats?.mastered ?? 0;
    const pct = total > 0 ? Math.round((mastered / total) * 100) : 0;
    const status = stats?.overallStatus || (total === 0 ? null : "not_started");
    const label = status ? getMasteryLabel(status) : "—";
    const color = status ? getMasteryColorByStatus(status) : "#94a3b8";

    return (
        <div className="cdm-diff-row">
            <div className="cdm-diff-left">
                <span className="cdm-dot" style={{ background: meta.color }} />
                <div>
                    <div className="cdm-diff-name">{meta.label}</div>
                    <div className="cdm-diff-sub" style={{ color: meta.color }}>({meta.sub})</div>
                </div>
            </div>
            <div className="cdm-diff-right">
                <div className="cdm-diff-score">{mastered} / {total}</div>
                <div className="cdm-bar-track">
                    <div className="cdm-bar-fill"
                        style={{ width: `${pct}%`, background: meta.color }}
                    />
                </div>
                <div className="cdm-diff-status" style={{ color: total === 0 ? "#cbd5e1" : color }}>
                    {total === 0 ? "No frames" : label}
                </div>
            </div>
        </div>
    );
}

export default function ChapterDetailModal({
    chapter,
    chapterStats,
    loading,
    onClose,
    onResume,
    onExplore,
}) {
    const overlayRef = useRef(null);

    useEffect(() => {
        function onKey(e) { if (e.key === "Escape") onClose(); }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, []);

    function handleOverlayClick(e) {
        if (e.target === overlayRef.current) onClose();
    }

    const totalFrames = chapterStats?.totalFrames || 0;
    const masteredFrames = chapterStats?.masteredFrames || 0;
    const lotCount = chapterStats?.LOT?.totalFrames || 0;
    const motCount = chapterStats?.MOT?.totalFrames || 0;
    const hotCount = chapterStats?.HOT?.totalFrames || 0;
    const recommendedNext = chapterStats?.recommendedNext || null;

    return (
        <div className="cdm-overlay" ref={overlayRef} onClick={handleOverlayClick}>
            <div className="cdm-card" role="dialog" aria-modal="true">

                {/*--- Header ---*/}
                <div className="cdm-header">
                    <div>
                        <span className="cdm-badge">📖 Chapter</span>
                        <h2 className="cdm-title">{chapter?.title || "Chapter"}</h2>
                        <p className="cdm-subtitle">
                            {chapter?.grade || "Grade"} - {totalFrames} Frame{totalFrames !== 1 ? "s" : ""}
                        </p>
                    </div>
                    <button className="cdm-close" onClick={onClose} aria-label="Close"> ✕ </button>
                </div>

                {/*--- Body ---*/}
                {loading ? (
                    <div className="cdm-loading">
                        <div className="cdm-loading-spinner" />
                        <span>Loading mastery data…</span>
                    </div>
                ) : (
                    <div className="cdm-body">
                        {/* Left — donut */}
                        <div className="cdm-left">
                            <DonutArc masteredFrames={masteredFrames} totalFrames={totalFrames} overallStatus={chapterStats?.overallStatus} />
                            <p className="cdm-frame-count">{masteredFrames} / {totalFrames} Frames Mastered</p>
                        </div>
                        {/* Right — difficulty breakdown (LOT / MOT / HOT) */}
                        <div className="cdm-right">
                            <DiffRow diff="LOT" stats={chapterStats?.LOT} totalCount={lotCount} />
                            <DiffRow diff="MOT" stats={chapterStats?.MOT} totalCount={motCount} />
                            <DiffRow diff="HOT" stats={chapterStats?.HOT} totalCount={hotCount} />
                        </div>
                    </div>
                )}

                {/* ── Footer ── */}
                <div className="cdm-footer">
                    <div className="cdm-rec">
                        <div className="cdm-rec-icon">🎯</div>
                        <div className="cdm-rec-text">
                            <div className="cdm-rec-label">Recommended Next</div>
                            {recommendedNext ? (
                                <>
                                    <div className="cdm-rec-title">{recommendedNext.frameTitle}</div>
                                    <div className="cdm-rec-sub">
                                        Continue your progress in {recommendedNext.difficulty} level
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="cdm-rec-title">
                                        {masteredFrames === totalFrames && totalFrames > 0
                                            ? "All frames mastered! 🎉"
                                            : "Start practising"}
                                    </div>
                                    <div className="cdm-rec-sub">Keep up the momentum!</div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="cdm-actions">
                        {recommendedNext && (
                            <button className="cdm-btn-resume" onClick={() => onResume(recommendedNext)}>
                                ▶ Resume
                            </button>
                        )}
                        <button className="cdm-btn-explore" onClick={onExplore}>
                            🧭 Explore
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}